import Select from '../ui/Select'
import { useState } from 'react'

import type { ColumnInfo } from '../api/client'
import { labelTable } from './BoardCell'

/** 面板里可选的两种尺寸（px） */
const ITEM_HEIGHTS = [36, 60] as const
const HEADER_HEIGHTS = [36, 44, 52] as const
/** 名称列之后额外冻结几列（0..3） */
const PINNED_COUNTS = [0, 1, 2, 3] as const
/** backlog #5：组与组之间的垂直间隙（px） */
const GROUP_GAPS = [8, 16, 24, 32] as const

export interface BoardSettingsPanelProps {
  /** 当前生效值 */
  itemHeight: number
  headerHeight: number
  /** 名称列之后额外冻结的列数 */
  pinned: number
  /** 组间隙（px） */
  groupGap: number
  /** 看板全部列（用于默认值编辑器） */
  columns: ColumnInfo[]
  /** 当前生效的默认条目取值：columnKey -> 值 */
  defaults: Record<string, string>
  busy: boolean
  onCancel: () => void
  /** 只交出改动过的项 */
  onSave: (patch: {
    ItemHeight?: number
    HeaderHeight?: number
    PinnedColumns?: number
    GroupGap?: number
    DefaultItemValues?: Record<string, string>
  }) => void
}

/**
 * 显示设置面板（M8）。
 *
 * 只做已经能真正生效的两项：行高（ItemHeight）与表头高（HeaderHeight）。
 * 另外三项（Pin columns / Default item values / Conditional coloring）先占位置灰 ——
 * 条件着色尤其是一个独立子项（规则模型 + 求值 + 编辑 UI），不在这里实现。
 */
export default function BoardSettingsPanel({
  itemHeight,
  headerHeight,
  pinned,
  groupGap,
  columns,
  defaults,
  busy,
  onCancel,
  onSave,
}: BoardSettingsPanelProps) {
  const [ih, setIh] = useState<number>(itemHeight)
  const [hh, setHh] = useState<number>(headerHeight)
  const [pin, setPin] = useState<number>(pinned)
  const [gap, setGap] = useState<number>(groupGap)
  const [defs, setDefs] = useState<Record<string, string>>(defaults)

  const save = () => {
    const patch: {
      ItemHeight?: number
      HeaderHeight?: number
      PinnedColumns?: number
      GroupGap?: number
      DefaultItemValues?: Record<string, string>
    } = {}
    if (ih !== itemHeight) patch.ItemHeight = ih
    if (hh !== headerHeight) patch.HeaderHeight = hh
    if (pin !== pinned) patch.PinnedColumns = pin
    if (gap !== groupGap) patch.GroupGap = gap
    const cleanDefs: Record<string, string> = {}
    for (const [k, v] of Object.entries(defs)) if (v !== '') cleanDefs[k] = v
    if (JSON.stringify(cleanDefs) !== JSON.stringify(defaults)) patch.DefaultItemValues = cleanDefs
    onSave(patch)
  }

  return (
    <>
      <div className="settings-mask" onClick={onCancel} />
      <div className="settings-panel" role="dialog" aria-modal="true" aria-label="显示设置">
        <header className="settings-panel__head">
          <span className="settings-panel__title">显示设置</span>
        </header>

        <div className="settings-panel__body">
          <section className="settings-group">
            <h3 className="settings-group__title">行高（Item height）</h3>
            <p className="settings-group__desc">控制数据行的高度。名称列与所有单元格一起变高。</p>
            <div className="settings-opts">
              {ITEM_HEIGHTS.map((h) => (
                <button
                  key={h}
                  type="button"
                  className={'settings-opt' + (ih === h ? ' settings-opt--on' : '')}
                  disabled={busy}
                  onClick={() => setIh(h)}
                >
                  {h}px
                </button>
              ))}
            </div>
          </section>

          <section className="settings-group">
            <h3 className="settings-group__title">表头高（Header height）</h3>
            <p className="settings-group__desc">只影响列标题行，不影响数据行。</p>
            <div className="settings-opts">
              {HEADER_HEIGHTS.map((h) => (
                <button
                  key={h}
                  type="button"
                  className={'settings-opt' + (hh === h ? ' settings-opt--on' : '')}
                  disabled={busy}
                  onClick={() => setHh(h)}
                >
                  {h}px
                </button>
              ))}
            </div>
          </section>

          <section className="settings-group">
            <h3 className="settings-group__title">冻结列（Pin columns）</h3>
            <p className="settings-group__desc">
              名称列始终冻结。这里控制名称列之后<strong>额外</strong>冻结几列，横向滚动时它们保持可见。
            </p>
            <div className="settings-opts">
              {PINNED_COUNTS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={'settings-opt' + (pin === n ? ' settings-opt--on' : '')}
                  disabled={busy}
                  onClick={() => setPin(n)}
                >
                  {n === 0 ? '不额外冻结' : `${n} 列`}
                </button>
              ))}
            </div>
          </section>

          <section className="settings-group">
            <h3 className="settings-group__title">组间隙（Group gap）</h3>
            <p className="settings-group__desc">
              分组与分组之间的垂直空白。间隙同时是拖动分组的放置区（拖到上一组下半部分即插到它之后）。
            </p>
            <div className="settings-opts">
              {GROUP_GAPS.map((g) => (
                <button
                  key={g}
                  type="button"
                  className={'settings-opt' + (gap === g ? ' settings-opt--on' : '')}
                  disabled={busy}
                  onClick={() => setGap(g)}
                >
                  {g}px
                </button>
              ))}
            </div>
          </section>

          <section className="settings-group">
            <h3 className="settings-group__title">默认条目取值（Default item values）</h3>
            <p className="settings-group__desc">
              新建条目时自动填入这些列。留空表示该列不设默认。状态/下拉按标签选择。
            </p>
            <div className="settings-defs">
              {columns
                .filter((c) => c.key !== 'name')
                .filter((c) =>
                  ['text', 'long-text', 'numeric', 'date', 'color', 'dropdown'].includes(c.type),
                )
                .map((c) => {
                  const isLabelCol = c.type === 'color' || c.type === 'dropdown'
                  const t = isLabelCol
                    ? labelTable(c)
                    : { text: {} as Record<string, string>, color: {} as Record<string, string>, keys: [] as string[] }
                  return (
                    <div className="settings-defrow" key={c.id}>
                      <span className="settings-defrow__name" title={c.title}>
                        {c.title}
                      </span>
                      {isLabelCol ? (
                        <Select
                          className="settings-defrow__input"
                          value={defs[c.key] ?? ''}
                          disabled={busy}
                          onChange={(v) => setDefs({ ...defs, [c.key]: v })}
                        >
                          <option value="">（不设）</option>
                          {t.keys.map((k) => (
                            <option key={k} value={k}>
                              {t.text[k] ?? k}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <input
                          className="settings-defrow__input"
                          value={defs[c.key] ?? ''}
                          disabled={busy}
                          placeholder="不设"
                          onChange={(e) => setDefs({ ...defs, [c.key]: e.target.value })}
                        />
                      )}
                    </div>
                  )
                })}
            </div>
          </section>

          <section className="settings-group settings-group--todo">
            <h3 className="settings-group__title">
              条件着色（Conditional coloring）<span className="settings-tag">后续单独做</span>
            </h3>
            <p className="settings-group__desc">
              按规则给整行或单元格上色（条件 + 颜色 + 优先级），需要独立的规则编辑器。
            </p>
          </section>
        </div>

        <footer className="settings-panel__foot">
          <button type="button" className="settings-btn" disabled={busy} onClick={onCancel}>
            取消
          </button>
          <button
            type="button"
            className="settings-btn settings-btn--primary"
            disabled={busy}
            onClick={save}
          >
            保存
          </button>
        </footer>
      </div>
    </>
  )
}
